import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, FaDirections } from 'react-icons/fa';
import { supabase } from '../lib/supabaseClient';

const ContactItem = ({ icon, label, children }) => (
  <div className="flex items-start gap-4 p-4 rounded-xl transition-colors duration-200 hover:bg-gray-50">
    <div
      className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
      style={{ background: 'rgba(20, 184, 166, 0.1)' }}
    >
      <span className="text-teal-500 text-base">{icon}</span>
    </div>
    <div>
      <p className="text-xs uppercase tracking-wider text-gray-400 mb-1 font-medium">{label}</p>
      <div className="text-gray-700">{children}</div>
    </div>
  </div>
);

const Contact = () => {
  const [settings, setSettings] = useState({
    contact_email: 'cargando...',
    contact_phones: '[]'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value');

      if (error) {
        console.error('Error fetching site settings:', error);
      } else {
        const settingsMap = {};
        data.forEach(setting => {
          settingsMap[setting.key] = setting.value;
        });
        setSettings(settingsMap);
      }
    };
    fetchSettings();
  }, []);

  const mapAddress = encodeURIComponent('place_id:ChIJ_Z7e8a93dZERuXm_G9q17hQ');
  const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${mapAddress}&center=-6.5243076,-76.3752341&zoom=17`;

  let phones = [];
  try {
    phones = JSON.parse(settings.contact_phones);
  } catch (e) {
    console.error("Could not parse contact_phones JSON:", settings.contact_phones);
  }

  return (
    <section id="contacto" className="py-20 relative overflow-hidden" style={{ background: '#ffffff' }}>
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 80% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
            'radial-gradient(ellipse at 50% 80%, rgba(20, 184, 166, 0.2) 0%, transparent 65%)',
            'radial-gradient(ellipse at 20% 50%, rgba(20, 184, 166, 0.25) 0%, transparent 65%)',
          ],
        }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 30% 70%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
            'radial-gradient(ellipse at 60% 20%, rgba(196, 173, 121, 0.15) 0%, transparent 55%)',
            'radial-gradient(ellipse at 70% 30%, rgba(196, 173, 121, 0.2) 0%, transparent 55%)',
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        style={{ zIndex: 1 }}
      />

      <div className="container mx-auto px-4 relative" style={{ zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-sm uppercase tracking-[0.3em] mb-3 font-medium"
            style={{ color: '#c4ad79' }}
          >
            Contacto
          </motion.p>
          <h2 className="text-4xl md:text-5xl font-playfair font-extrabold text-gray-900">Encuéntranos</h2>
          <p className="mt-4 text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            Estamos ubicados en la selva de San Martín, a pocos minutos de Tarapoto.
          </p>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3, ease: 'easeOut' }}
            className="mx-auto mt-5"
            style={{
              width: '60px',
              height: '2px',
              background: '#c4ad79',
              transformOrigin: 'center',
              borderRadius: '2px',
            }}
          />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10 max-w-6xl mx-auto items-stretch">
          {/* Contact info - 2 cols */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 bg-white rounded-2xl overflow-hidden flex flex-col"
            style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' }}
          >
            <div className="p-6 flex flex-col flex-grow">
              <h3 className="text-xl font-playfair font-bold text-gray-800 mb-1">Información</h3>
              <p className="text-sm text-gray-400 mb-4">Comunícate con nosotros</p>

              <div className="space-y-1 flex-grow">
                <ContactItem icon={<FaMapMarkerAlt />} label="Dirección">
                  <p className="text-sm font-medium">Carretera a Sta Rosa</p>
                  <p className="text-xs text-gray-400">Tarapoto 22200, San Martín</p>
                </ContactItem>

                <ContactItem icon={<FaPhone />} label="Teléfonos">
                  {phones.map((phone, index) => (
                    <a key={index} href={`tel:${phone}`} className="block text-sm font-medium hover:text-teal-600 transition-colors">{phone}</a>
                  ))}
                </ContactItem>

                <ContactItem icon={<FaEnvelope />} label="Email">
                  <a href={`mailto:${settings.contact_email}`} className="text-sm font-medium hover:text-teal-600 transition-colors">{settings.contact_email}</a>
                </ContactItem>

              </div>
            </div>

            {/* Buttons */}
            <div className="px-6 pb-6 space-y-3">
              <motion.a
                href="https://wa.me/51947791970?text=Hola%2C%20me%20gustar%C3%ADa%20obtener%20m%C3%A1s%20informaci%C3%B3n%20sobre%20Sumaq%20Mayu."
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02, boxShadow: '0 10px 25px rgba(37, 211, 102, 0.3)' }}
                whileTap={{ scale: 0.98 }}
                className="w-full text-white py-3 px-6 text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-all duration-300"
                style={{ background: '#25D366' }}
              >
                <FaWhatsapp className="text-lg" /> Escríbenos por WhatsApp
              </motion.a>
              <motion.a
                href="https://www.google.com/maps/place/Sumaq+Mayu+-+Hotel+y+Centro+de+Convenciones/@-6.5243076,-76.3752341,17z"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-3 px-6 text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-all duration-300 border"
                style={{ color: '#14B8A6', borderColor: '#14B8A6' }}
              >
                <FaDirections className="text-lg" /> Cómo llegar
              </motion.a>
            </div>
          </motion.div>

          {/* Map - 3 cols */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="lg:col-span-3 rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)', minHeight: '450px' }}
          >
            <iframe
              title="Sumaq Mayu Location"
              className="w-full h-full border-0"
              style={{ minHeight: '450px' }}
              src={mapUrl}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
